import React, { useState } from 'react';
import { Play, Mic, Youtube, Music2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useData } from '../context/DataContext';

const PodcastsPage = () => {
  const { data, loading } = useData();
  const [selectedTheme, setSelectedTheme] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const themes = ['All', 'Fundraising', 'Strategy', 'Founder Journeys'];

  if (loading || !data || !data.podcastsData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  const episodes = data.podcastsData;

  const filteredEpisodes = selectedTheme === 'All'
    ? episodes
    : episodes.filter(ep => ep.theme === selectedTheme);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      {/* Hero Section */}
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">Insights</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Podcasts
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Conversations with founders, investors, and business leaders on fundraising, strategy, and the entrepreneurial journey
            </p>
          </div>
        </div>
      </section>

      {/* Theme Filter */}
      <section className="py-12 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto flex flex-wrap gap-3 justify-center">
            {themes.map(theme => (
              <Button
                key={theme}
                variant={selectedTheme === theme ? 'default' : 'outline'}
                className={selectedTheme === theme ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-600 text-slate-300 hover:bg-slate-800'}
                onClick={() => setSelectedTheme(theme)}
              >
                {theme}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Episodes List */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-6">
            {filteredEpisodes.map((episode) => (
              <Card key={episode.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      {episode.youtubeId ? (
                        <div className="w-24 h-24 rounded-xl overflow-hidden bg-slate-800 border border-slate-700">
                          <img
                            src={`https://img.youtube.com/vi/${episode.youtubeId}/hqdefault.jpg`}
                            alt={episode.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          <Mic className="h-12 w-12 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="border-amber-500/30 text-amber-500">
                              {episode.theme}
                            </Badge>
                            <span className="text-slate-400 text-sm">{episode.duration}</span>
                          </div>
                          <h3 className="text-xl font-semibold text-white mb-2">{episode.title}</h3>
                          {episode.guest && (
                            <p className="text-slate-400 text-sm mb-2">
                              Guest: <span className="text-amber-500">{episode.guest}</span>
                            </p>
                          )}
                          <p className="text-slate-400 text-sm leading-relaxed">{episode.description}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-4">
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => episode.youtubeId ? setSelectedVideo(episode.youtubeId) : window.open(episode.youtubeUrl, '_blank')}
                        >
                          <Youtube className="h-4 w-4 mr-2" />
                          YouTube
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredEpisodes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">No episodes found for this theme</p>
            </div>
          )}
        </div>
      </section>
      <Footer />

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4">
          <button
            onClick={() => setSelectedVideo(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-500 z-10"
          >
            <X className="h-8 w-8" />
          </button>
          <div className="w-full max-w-5xl aspect-video rounded-xl overflow-hidden bg-black/50 shadow-2xl">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastsPage;
